// TS twin of iOS WheelDetectionKit `WheelSizeOptions` (0.7.0, ?use_ios_ui).
//
// The sizes one placed wheel can be shown at. The client's rule: a wheel
// snaps only to the diameters its payload lists — nothing between, nothing
// outside. A resize scales the model already loaded, across the face only.

import { GLB_LIP_ALLOWANCE_IN } from '@/models/base-diameter'

export type TWheelSizeOptions =
  /** Two or more diameters, smallest first; `reference` (one of them) is
   *  the size the wheel was placed at, and every resize scales from it. */
  | { readonly kind: 'choice'; readonly sizes: readonly number[]; readonly reference: number }
  /** Exactly one diameter. Shown, never changed. */
  | { readonly kind: 'fixed'; readonly inches: number }
  /** No published diameter: what the model measures as. Shown, never changed. */
  | { readonly kind: 'estimated'; readonly inches: number }
  /** Nothing to show and nothing to scale from. */
  | { readonly kind: 'hidden' }

export function wheelSizeOptions({
  listed,
  current,
  measured,
}: {
  /** Payload diameters, in payload order, duplicates and all. */
  listed: readonly number[]
  /** The diameter the wheel opens at, or null when the payload gives none. */
  current: number | null
  /** What the model measures as, whole inches, or null. */
  measured: number | null
}): TWheelSizeOptions {
  // The wheel is placed at `current`, so it can be shown at it whether or
  // not the list says so.
  const sizes = [...new Set([...listed, ...(current == null ? [] : [current])].filter(isSize))].sort(
    (a, b) => a - b,
  )
  if (sizes.length === 0) {
    return measured != null && isSize(measured) ? { kind: 'estimated', inches: measured } : { kind: 'hidden' }
  }
  if (sizes.length === 1) return { kind: 'fixed', inches: sizes[0]! }
  // No current size: the listed size nearest the measurement (tie → the
  // smaller). Neither: any reference would be a guess, so hide.
  const reference = current ?? (measured == null ? null : nearest(sizes, measured))
  return reference == null ? { kind: 'hidden' } : { kind: 'choice', sizes, reference }
}

/** Where a new placement starts and what every resize scales from. Null
 *  when no published size gives one. */
export function sizeOptionsReference(options: TWheelSizeOptions): number | null {
  switch (options.kind) {
    case 'choice':
      return options.reference
    case 'fixed':
      return options.inches
    default:
      return null
  }
}

/**
 * The scale that shows a wheel placed at `from` inches at `to` inches.
 * The face takes the ratio of EDGE-TO-EDGE diameters (the model is true
 * geometry); the width stays 1 — the diameter is the only thing that changes.
 */
export function sizeScale({ from, to }: { from: number; to: number }): { face: number; width: number } {
  return { face: (to + GLB_LIP_ALLOWANCE_IN) / (from + GLB_LIP_ALLOWANCE_IN), width: 1 }
}

/** The container-local width that draws at `metres` inside a container
 *  scaled by `faceScale` across its face. */
export function localWidth({ metres, faceScale }: { metres: number; faceScale: number }): number {
  return metres / faceScale
}

/** `20`, or `17.5` for the half sizes that exist. No inch mark. */
export function sizeDisplayText(inches: number): string {
  return String(inches)
}

/** The listed size nearest `inches`; halfway between two, the smaller. */
export function nearest(sizes: readonly number[], inches: number): number | null {
  let best: number | null = null
  for (const size of sizes) {
    if (best == null || Math.abs(size - inches) < Math.abs(best - inches)) best = size
  }
  return best
}

function isSize(value: number): boolean {
  return Number.isFinite(value) && value > 0
}
